import {
  NotificationTypeEnum,
  useNotification,
} from '@prismamedia/one-components';
import { useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
// import { grey } from '@mui/material/colors';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { useQuery } from '@apollo/client';
import { GET_RECIPE_CATEGORIES } from '../../apollo/queries/getRecipeCategories.recipe.graphql';
import { GetRecipeCategories } from '../../__generated__/queries-recipe';
import { useGenerateSlideshowIA } from '../../apollo/mutations/generateSlideshow.web.graphql';
import {
  BrandKey,
  RecipeCost,
  RecipeDifficulty,
} from '../../__generated__/queries-web';

// const textareaComponentStyle = {
//   borderColor: grey[400],
//   borderRadius: '8px',
//   padding: '8px 12px',
//   width: '100%',
// };

const recipeCostMap: {
  [Key in keyof typeof RecipeCost]: string;
} = {
  ASSEZ_CHER: 'Assez cher',
  BON_MARCHE: 'Bon marché',
  CHER: 'Cher',
  MOYEN: 'Moyen',
};

// We enforce the type of the keys to be the same as the RecipeCost enum coming from api
const difficultyTypeMap: {
  [Key in keyof typeof RecipeDifficulty]: string;
} = {
  TRES_FACILE: 'Très facile',
  FACILE: 'Facile',
  MOYEN: 'Moyen',
  CONIFRME: 'Confirmé',
  DIFFICILE: 'Difficile',
};

export const SlideshowGenerateForm = ({
  brandKey,
  setActiveTab,
}: {
  brandKey: BrandKey;
  setActiveTab: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const [slideshowSubject, setSlideshowSubject] = useState<string | null>(null);
  const [slideshowLength, setDiapoLength] = useState<number>(0);
  const [recipeDifficulty, setRecipeDifficulty] = useState<RecipeDifficulty[]>(
    [],
  );
  const [ingredients, setIngredients] = useState<string[]>([]);
  const [recipeCost, setRecipeCost] = useState<RecipeCost[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const { data } = useQuery<GetRecipeCategories>(GET_RECIPE_CATEGORIES);
  const [generateDiapoIA] = useGenerateSlideshowIA();
  const { pushNotification } = useNotification();

  return (
    <form
      onSubmit={(formEvent) => {
        formEvent.preventDefault();
        if (!slideshowSubject || !slideshowLength) {
          return pushNotification({
            type: NotificationTypeEnum.error,
            message: 'Le sujet et la longueur du diaporama sont obligatoires.',
          });
        }
        try {
          generateDiapoIA({
            variables: {
              ...{
                slideshowLength,
                slideshowSubject,
                brandKey,
              },
              ...(ingredients.length > 0 && { recipeIngredients: ingredients }),
              ...(categories.length > 0 && { recipeCategories: categories }),
              ...(recipeCost.length > 0 ? { recipeCost } : {}),
              ...(recipeDifficulty.length > 0 && { recipeDifficulty }),
            },
          });
          setActiveTab(1);
        } catch (e) {
          pushNotification({
            type: NotificationTypeEnum.error,
            message: 'Erreur lors de la génération du diaporama',
          });
        }
      }}
    >
      <Card
        variant="outlined"
        sx={{
          padding: '40px',
          display: 'flex',
          flexDirection: 'column',
          gap: '32px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: '8px',
          }}
        >
          <AutoFixHighIcon
            fontSize="small"
            sx={{ marginRight: '2px', color: 'text.secondary' }}
          />
          <Typography
            sx={{ fontSize: 14, paddingTop: '1.5px' }}
            color="text.secondary"
            gutterBottom
          >
            Paramètres du diaporama
          </Typography>
        </Box>
        <Box>
          <FormControl sx={{ minWidth: '45%' }}>
            <InputLabel id="diapoType">Type de diaporama</InputLabel>
            <Select
              key={'1'}
              label="Type de diaporama"
              labelId="diapoType"
              value={'recipe'}
              onChange={() => {}}
            >
              <MenuItem key={'1'} value={'recipe'}>
                Recette
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box
          sx={{
            display: 'flex',
            // flexDirection: 'column',
            justifyContent: 'space-between',
            gap: '16px',
          }}
        >
          <TextField
            sx={{
              width: '45%',
            }}
            id="outlined-error"
            type="number"
            label="Nombre de recettes"
            value={slideshowLength}
            onChange={(e) => setDiapoLength(parseInt(e.target.value, 10))}
            required
          />
          <TextField
            sx={{
              width: '45%',
            }}
            id="outlined-error-helper-text"
            label="Sujet du diaporama"
            placeholder=""
            required
            value={slideshowSubject}
            onChange={(e) => setSlideshowSubject(e.target.value)}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            // flexDirection: 'column',
            justifyContent: 'space-between',
            gap: '16px',
          }}
        >
          {' '}
          <FormControl sx={{ width: '45%' }}>
            <InputLabel id="diapoType">Prix des recettes</InputLabel>
            <Select
              multiple
              key={'diapoType'}
              label="Type de diaporama"
              labelId="diapoType"
              value={recipeCost}
              onChange={(e) => setRecipeCost(e.target.value as RecipeCost[])}
            >
              <MenuItem key={'Bon marché'} value={RecipeCost.BON_MARCHE}>
                {recipeCostMap[RecipeCost.BON_MARCHE]}
              </MenuItem>
              <MenuItem key={'Moyen'} value={RecipeCost.MOYEN}>
                {recipeCostMap[RecipeCost.MOYEN]}
              </MenuItem>
              <MenuItem key={'Assez Cher'} value={RecipeCost.ASSEZ_CHER}>
                {recipeCostMap[RecipeCost.ASSEZ_CHER]}
              </MenuItem>
              <MenuItem key={'Cher'} value={RecipeCost.CHER}>
                {recipeCostMap[RecipeCost.CHER]}
              </MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ width: '45%' }}>
            <InputLabel id="difficulty">
              Difficulté d'éxécution des recettes
            </InputLabel>
            <Select
              multiple
              key={'difficulty'}
              label="Type de diaporama"
              labelId="difficulty"
              value={recipeDifficulty}
              onChange={(e) =>
                setRecipeDifficulty(e.target.value as RecipeDifficulty[])
              }
            >
              <MenuItem
                key={'très facile'}
                value={RecipeDifficulty.TRES_FACILE}
              >
                {difficultyTypeMap[RecipeDifficulty.TRES_FACILE]}
              </MenuItem>
              <MenuItem key={'facile'} value={RecipeDifficulty.FACILE}>
                {difficultyTypeMap[RecipeDifficulty.FACILE]}
              </MenuItem>
              <MenuItem key={'moyen'} value={RecipeDifficulty.MOYEN}>
                {difficultyTypeMap[RecipeDifficulty.MOYEN]}
              </MenuItem>
              <MenuItem key={'difficile'} value={RecipeDifficulty.DIFFICILE}>
                {difficultyTypeMap[RecipeDifficulty.DIFFICILE]}
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box
          sx={{
            display: 'flex',
            // flexDirection: 'column',
            justifyContent: 'space-between',
            gap: '16px',
          }}
        >
          <Autocomplete
            multiple
            freeSolo
            sx={{
              width: '45%',
            }}
            options={[]}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" label="Ingrédients" />
            )}
            value={ingredients}
            onChange={(event, newValue) => setIngredients(newValue as string[])}
          />
          <Autocomplete
            sx={{ width: '45%' }}
            multiple
            options={data?.categories.map((category) => category.title) || []}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Catégorie des recettes"
                variant="outlined"
              />
            )}
            value={categories}
            onChange={(event, newValue) => {
              setCategories(newValue);
            }}
          />
        </Box>
        {/* <Box
          sx={{
            display: 'flex',
            gap: '8px',
          }}
        >
          <AssistantIcon
            fontSize="small"
            sx={{ marginRight: '2px', color: 'text.secondary' }}
          />
          <Typography sx={{ fontSize: 14 }} color="text.secondary" gutterBottom>
            Prompts
          </Typography>
        </Box>
        <Box
          sx={{
            width: '100%',
          }}
        >
          <InputLabel shrink>Titre du diaporama</InputLabel>
          <TextareaAutosize
            key={'titre'}
            id="titre"
            minRows={6}
            value={promptTitle}
            onChange={(e) => setPromptTitle(e.target.value)}
            style={textareaComponentStyle}
          />
        </Box>
        <Box
          sx={{
            width: '100%',
          }}
        >
          <InputLabel shrink>Description d'une image du diaporama</InputLabel>
          <TextareaAutosize
            key={'image'}
            id="image"
            minRows={6}
            value={promptDescription}
            onChange={(e) => setPromptDescription(e.target.value)}
            style={textareaComponentStyle}
          />
        </Box> */}
        <DialogActions>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <Button
              data-testid="confirmBtn"
              color="primary"
              onClick={() => null}
              variant="contained"
              type="submit"
            >
              {'Générer le diaporama'}
            </Button>
          </Box>
        </DialogActions>
      </Card>
    </form>
  );
};
